import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Injectable, Injector } from '@angular/core';

import * as Sentry from '@sentry/browser';
import { RewriteFrames } from '@sentry/integrations';

import { AccountBase, SentryConfig } from './config';

@Injectable()
export class RtcErrorHandler implements ErrorHandler {
  private readonly filteredStatuses: number[] = [0, 401, 403, 404];
  private readonly appRevision: string = this.injector.get(this.sentryConfig.revisionToken);

  constructor(
    private account: AccountBase,
    private sentryConfig: SentryConfig,
    private injector: Injector,
  ) {
    if (!this.sentryConfig.enable) {
      return;
    }

    Sentry.init({
      dsn: this.sentryConfig.dsn,
      release: this.appRevision,
      whitelistUrls: [
        /https:\/\/(dxouce9bndnyz|d2m5u00bfeg39x)\.cloudfront\.net\/(.*).js/,
        /https?:\/\/(.+\.)?recruitee\.com\/(.*)\/(.*)\.js/,
        /http:\/\/app.recruitee.local:3003\/(.*)\.js/,
      ],
      integrations: [
        new RewriteFrames({
          iteratee: frame => {
            if (frame.filename) {
              frame.in_app = /(.*)(runtime|main|common|\d+)\.(.*)\.js/.test(frame.filename);
            }
            return frame;
          },
        }),
      ],
      beforeBreadcrumb: (
        breadcrumb: Sentry.Breadcrumb,
        hint?: Record<string, string>,
      ): Sentry.Breadcrumb | null =>
        // This will filter out clicks/typing
        breadcrumb.category && breadcrumb.category.includes('ui.') ? null : breadcrumb,
      beforeSend: (
        event: Sentry.Event,
        hint?: Sentry.EventHint,
      ): PromiseLike<Sentry.Event | null> | Sentry.Event | null => {
        if (!this.sentryConfig.enable) return null;

        const httpStatus = event.extra?.__serialized__?.status;
        const isStatusValid =
          httpStatus === undefined || !this.filteredStatuses.includes(httpStatus);

        return isStatusValid ? event : null;
      },
      maxBreadcrumbs: 30,
    });

    /**
     * NgRx effects service requires ErrorService
     * Here we need currentAdmin but it is empty because CurrentAdminEffects is not initialized yet
     * setTimeout fixes this issue
     */
    setTimeout(() => {
      this.account.currentAdmin$.subscribe(currentAdmin =>
        Sentry.setUser({
          id: `${currentAdmin.id}`,
          email: currentAdmin.email,
          username: `${currentAdmin.firstName} ${currentAdmin.lastName}`,
          roleName: currentAdmin.roleName,
        }),
      );

      this.account.currentCompany$.subscribe(currentCompany =>
        Sentry.setExtra('Company', currentCompany?.id),
      );
    });
  }

  public handleError(err: any): void {
    try {
      if (this.sentryConfig.enable) {
        if (err instanceof HttpErrorResponse) {
          if (!this.filteredStatuses.includes(err.status)) {
            Sentry.captureMessage(this.getMessage(err));
          }
        } else {
          this.sendErrorToSentry(err);
        }
      }
    } finally {
      if (this.sentryConfig.throwOnErrors) {
        this.throwError(err);
      }
    }
  }

  public trace(name: string): void {
    try {
      throw new Error(name);
    } catch (e) {
      this.handleError(e);
    }
  }

  private getMessage(response: HttpErrorResponse): string {
    return `Http request failed. (${response.url}, ${response.status}, ${response.message})`;
  }

  private sendErrorToSentry(err: any): void {
    Sentry.setTags({ fatal: 'true' });
    Sentry.captureException(err.originalError || err);
  }

  private throwError(err: any): void {
    // We could leave only console.error here, but
    // but the stacktrace is shown in a better form with `throw`
    // On the other hand we can't get rid of `console.error`,
    // because sometimes the errors are silently catched and never thrown
    // (f.e. try throwing an error in BaseRepo.updateBucketStateWithRequest before bucket.setData
    //  - seems like rxjs is catching and swallowing the error)
    // So for now, we have to live with errors being errored to the console twice
    try {
      console.error(err);
    } catch (_e) {
      // Ignore "TypeError: Converting circular structure to JSON" which appears in chrome sometimes
      // when trying to log the error in the console
    } finally {
      throw err;
    }
  }
}
